// Etiquetas, colores, fechas y bitácora de la pantalla Prospectos del portal del vendedor.
import {
    formatDateMx,
    formatShortDateMx,
    formatTimeMx,
    daysBetweenDays,
    mxDay,
} from '@/lib/dateHelpers';
import { prospectReasonText } from '@/domains/sales/wizard/prospects';
import type { UnreadCreditState } from '../../customers/lib/customerCreditFacts';
import type { UnreadRejectionsState } from '../../customers/lib/sciRejections';
import { CREDIT_STATE_NOTE } from '../../customers/lib/customersFormat';
import type { MoneyAtStakeKind, Prospect, ProspectStatus } from '@/domains/sales/wizard/types';

/** Lo que se pinta donde el dato no existe. Un guion, no una celda vacía. */
export const PENDING_CELL = '—';

// Las razones por las que la pestaña Wizard muestra lo que muestra. El paso sale de dos fuentes: el
// prospecto abierto CON teléfono lo saca de `GET /wizard/sessions?phone=` (la venta VIVA de ese
// número); el resuelto y el que no tiene teléfono lo sacan de su `wizardSessionId`, leído por id.

/** Hay venta abierta: se pinta el paso, y esto explica qué hace «Reabrir wizard» con ella. */
export const WIZARD_ACTIVE_SESSION_NOTE =
    'Esta venta sigue abierta. «Reabrir wizard» entra por el teléfono del cliente y la retoma.';

/** La venta movió dinero: «Reabrir» siempre entra a la misma, nunca ofrece una nueva. */
export const WIZARD_MONEY_AT_STAKE_NOTE =
    'Esta venta tiene dinero en juego: «Reabrir wizard» retoma la misma venta.';

/** La venta solo tiene un crédito cotizado, sin cobro: «Reabrir» también entra a la misma. */
export const WIZARD_CREDIT_QUOTED_NOTE =
    'Esta venta tiene un crédito cotizado: «Reabrir wizard» retoma la misma venta.';

export interface MoneyAtStakeBadge {
    label: string;
    note: string;
}

/** Chip y nota según el dinero que retiene la venta; sin dinero, nada. */
export function moneyAtStakeBadge(
    kind: MoneyAtStakeKind | null | undefined,
): MoneyAtStakeBadge | null {
    if (kind === 'SALE') return { label: 'Dinero en juego', note: WIZARD_MONEY_AT_STAKE_NOTE };
    if (kind === 'CREDIT_QUOTED')
        return { label: 'Crédito cotizado', note: WIZARD_CREDIT_QUOTED_NOTE };
    return null;
}

/** El silo no vence la venta por inactividad (`exactUntil: null`). */
export const SAVED_SALE_NO_EXPIRY_TEXT = 'Venta guardada sin vencimiento';

/** La fecha ya pasó y el silo todavía no barrió la venta: «Reabrir» entra a la misma. */
export const SAVED_SALE_EXPIRED_TEXT = 'La venta guardada ya venció: se retoma la misma venta';

export interface SavedSaleNotice {
    text: string;
    expired: boolean;
}

/** Hasta cuándo se retoma la venta en la misma pantalla, como se lee en el encabezado de la ficha. */
export function savedSaleNotice(
    exactUntil: string | null,
    nowMs: number = Date.now(),
): SavedSaleNotice {
    if (!exactUntil) return { text: SAVED_SALE_NO_EXPIRY_TEXT, expired: false };
    if (Date.parse(exactUntil) <= nowMs) return { text: SAVED_SALE_EXPIRED_TEXT, expired: true };
    return {
        text: `Venta guardada hasta el ${formatDateMx(exactUntil)}, ${formatTimeMx(exactUntil)}`,
        expired: false,
    };
}

/** `found: false` — el caso mayoritario, y no significa que el prospecto haya perdido su sesión. */
export const WIZARD_NO_SESSION_NOTE =
    'No hay una venta empezada con este teléfono. Puede que nunca se haya iniciado, o que la que había ya se cerró o se canceló. «Reabrir wizard» arranca una nueva desde el paso 01.';

/** Aviso al reabrir sin teléfono: el wizard aterriza en el paso 01 con el campo vacío. */
export const WIZARD_NO_PHONE_NOTE =
    'Este prospecto no tiene teléfono capturado: el wizard abre en el paso 01 con el campo vacío y hay que pedírselo al cliente.';

/** La consulta está apagada: no se puede afirmar que no haya venta abierta, solo que no se miró. */
export const WIZARD_LOOKUP_OFF_NOTE =
    'Todavía no se puede consultar la venta en curso desde aquí: falta desplegar la búsqueda por teléfono. «Reabrir wizard» sí retoma la venta si el cliente tiene una abierta.';

/** La consulta es accesoria: si falla, no se afirma que la venta no exista. */
export const WIZARD_LOOKUP_FAILED_NOTE =
    'No se pudo consultar la venta en curso, así que no sabemos si hay una abierta. El resto de la ficha sigue vigente; vuelve a entrar a la pestaña para reintentar.';

/** La ficha resuelta guarda su venta: el paso se lee por id y es historia, no un pendiente. */
export const WIZARD_SETTLED_STEP_NOTE =
    'Esta ficha ya no está en tu agenda: el paso es donde quedó la venta, no algo que tengas que retomar. Por eso «Reabrir wizard» está apagado.';

/** La ficha abierta sin teléfono: el paso sale de su venta vinculada, pero nadie sabe si sigue viva. */
export const WIZARD_LINKED_STEP_NOTE =
    'Este es el paso donde quedó la venta de la que salió la ficha. Sin teléfono capturado no se puede saber si sigue abierta, y «Reabrir wizard» arranca una nueva desde el paso 01.';

/** Ni teléfono ni sesión vinculada: no hay con qué buscar la venta, y no es que la consulta falle. */
export const WIZARD_NO_SOURCE_NOTE =
    'Esta ficha no salió de una venta del wizard —se abrió a mano, o se guardó antes de que el wizard empezara a vincularlas—, así que no hay sesión que consultar ni paso que mostrar.';

/** La lectura por id falló: no se afirma que la venta no exista, solo que no se pudo leer. */
export const WIZARD_LINKED_LOOKUP_FAILED_NOTE =
    'No se pudo leer la venta de la que salió esta ficha, así que no sabemos en qué paso quedó. El resto de la ficha sigue vigente; vuelve a entrar a la pestaña para reintentar.';

// Las razones del bloque «Historial con SureKeep». Los créditos salen del expediente del acreditado
// (`GET /borrowers/portfolio`) y los rechazos de su historial de evaluaciones SCI
// (`GET /scoring/borrowers/{borrowerId}/evaluations`), que es una lectura aparte y accesoria.

/** El prospecto sin cliente registrado no tiene acreditado: no hay a quién preguntarle. */
export const CREDIT_NO_CUSTOMER_NOTE =
    'Este prospecto todavía no está registrado como cliente, así que no tiene expediente de crédito que consultar.';

/** Por qué la fila de rechazos quedó en guion. Un conteo LEÍDO —cero incluido— nunca llega acá. */
export const REJECTIONS_UNREAD_NOTE: Record<UnreadRejectionsState, string> = {
    loading: 'Consultando el historial de evaluaciones del cliente.',
    'no-customer': CREDIT_NO_CUSTOMER_NOTE,
    'no-borrower':
        'Este cliente todavía no tiene expediente de crédito en SureKeep, así que no hay evaluaciones que contar.',
    forbidden: 'Tu rol no puede leer el historial de evaluaciones del cliente.',
    error: 'No se pudo leer el historial de evaluaciones del cliente. El resto de la ficha sigue vigente; vuelve a entrar a la pestaña para reintentar.',
};

/** De dónde salen los créditos y dónde está la ficha completa del cliente. */
export const CREDIT_HISTORY_NOTE =
    'Los créditos salen del expediente de crédito del cliente. Su ficha 360° completa vive en Clientes: ábrela con el botón de arriba.';

/**
 * El cliente registrado cuya celda de cartera no vino. Red de seguridad: `loadCreditPortfolio`
 * devuelve una celda por cada id pedido, así que no debería verse nunca.
 */
export const CREDIT_MISSING_CELL_NOTE = 'No llegaron las cifras de crédito de este cliente.';

/**
 * El motivo del hueco. `no-source` lleva el suyo: el de Clientes habla de la búsqueda exacta y de
 * «no es de tu tienda», y en esta pantalla ninguna de las dos cosas existe.
 */
export function creditUnreadNote(state: UnreadCreditState): string {
    return state === 'no-source' ? CREDIT_MISSING_CELL_NOTE : CREDIT_STATE_NOTE[state];
}

/**
 * El motivo del prospecto resuelto es HISTORIA: el silo lo escribe cuando la venta se traba y no lo
 * vuelve a tocar, así que contradice al estado si se lee como pendiente vigente.
 */
export const SETTLED_REASON_NOTE =
    'Es el motivo con el que la ficha quedó pausada en su momento, no un pendiente de hoy: manda el estado.';

/** Etiqueta del motivo: en la ficha resuelta se nombra como lo que es, la pausa que ya pasó. */
export const SETTLED_REASON_LABEL = 'Motivo de la pausa';

/**
 * La venta trabada no se destraba desde el wizard: reabrirla lleva al mismo aviso. Prometer lo
 * contrario le cuesta al vendedor un viaje con el cliente enfrente.
 */
export const WIZARD_BLOCKED_REOPEN_NOTE =
    '«Reabrir wizard» la lleva al aviso, no al paso siguiente.';

/** Por qué no se ofrece la ficha 360: sin cliente registrado no hay expediente que abrir. */
export const CUSTOMER_360_NO_CUSTOMER_NOTE =
    'Este prospecto todavía no está registrado como cliente: no tiene ficha 360° que abrir.';

export const PROSPECT_STATUS_LABEL: Record<ProspectStatus, string> = {
    PENDING: 'Pendiente',
    UNDER_REVIEW: 'En revisión',
    READY_TO_RESUME: 'Lista para continuar',
    // El chip va al lado del nombre del cliente: «Completado» se leería como el cliente, no la venta.
    CLOSED: 'Venta completada',
    CANCELLED: 'Cancelado',
    LOST: 'Perdido',
};

/**
 * Chip outline por estado. El color dice DE QUIÉN ES EL TURNO, que es lo que el vendedor necesita
 * saber barriendo la lista con la vista:
 *
 * ámbar = te toca a ti · gris = esperas a un tercero · verde = terminó bien · rojo = terminó mal.
 */
export const PROSPECT_STATUS_CHIP_CLASS: Record<ProspectStatus, string> = {
    // El único estado abierto, y siempre es del vendedor: el ámbar dice que le toca a él.
    PENDING: 'border-warning text-warning',
    // No depende del vendedor: apagado a propósito, para que no compita con lo que sí es suyo.
    UNDER_REVIEW: 'border-muted-foreground text-muted-foreground',
    // Lo que la trababa se resolvió y volvió a ser suya: el único relleno de la lista, porque es la
    // fila que tiene que encontrar barriendo con la vista.
    READY_TO_RESUME: 'border-warning bg-warning-soft text-warning',
    CLOSED: 'border-success text-success',
    CANCELLED: 'border-destructive text-destructive',
    LOST: 'border-destructive text-destructive',
};

const UNKNOWN_STATUS_LABEL = 'Estado desconocido';
const NEUTRAL_CHIP_CLASS = 'border-muted-foreground text-muted-foreground';

/**
 * Traduce el estado con red de seguridad: llega del silo en runtime, así que TypeScript lo cree
 * exhaustivo pero un valor nuevo pintaría un hueco.
 */
export function statusLabel(status: ProspectStatus): string {
    return (
        (PROSPECT_STATUS_LABEL as Partial<Record<ProspectStatus, string>>)[status] ??
        UNKNOWN_STATUS_LABEL
    );
}

export function statusChipClass(status: ProspectStatus): string {
    return (
        (PROSPECT_STATUS_CHIP_CLASS as Partial<Record<ProspectStatus, string>>)[status] ??
        NEUTRAL_CHIP_CLASS
    );
}

/**
 * Estados que ya no se agendan: su fecha de contacto se lee como historia, no como pendiente.
 *
 * `UNDER_REVIEW` entra porque perseguir con «vencido hace 3 días» una venta que no depende del
 * vendedor le ensucia la bandeja, y tampoco se reabre: mientras el caso esté con un tercero, entrar
 * a la venta no la hace avanzar. La que se resolvió sale de acá y vuelve a ser del vendedor.
 */
export function isSettled(status: ProspectStatus): boolean {
    return (
        status === 'CLOSED' ||
        status === 'CANCELLED' ||
        status === 'LOST' ||
        status === 'UNDER_REVIEW'
    );
}

/** Lo que se lee en la ficha cuando el prospecto nunca registró un contacto. */
export const NO_CONTACT_TEXT = 'Sin contactar';

export type NextContactTone = 'overdue' | 'today' | 'plain';

export interface NextContactCell {
    text: string;
    tone: NextContactTone;
}

/**
 * Cómo se lee «Próx. contacto»: el prospecto resuelto muestra la fecha a secas; el abierto dice
 * cuánto se pasó o cuánto le falta, que es lo que el vendedor necesita de un vistazo.
 */
export function nextContactCell(
    nextContactAt: string,
    status: ProspectStatus,
    today: string,
): NextContactCell {
    if (isSettled(status) || !today) {
        return { text: formatShortDateMx(nextContactAt), tone: 'plain' };
    }
    const days = daysBetweenDays(mxDay(nextContactAt), today);
    if (days < 0) return { text: `venció ${String(-days)}d`, tone: 'overdue' };
    if (days === 0) return { text: 'Hoy', tone: 'today' };
    if (days === 1) return { text: 'Mañana', tone: 'plain' };
    return { text: formatShortDateMx(nextContactAt), tone: 'plain' };
}

/** `createdAt` llega como epoch en milisegundos; los formateadores de fecha piden un ISO. */
export function createdIso(prospect: Prospect): string {
    return new Date(prospect.createdAt).toISOString();
}

/** "1 prospecto" / "44 prospectos" — el contador de la barra de la tabla. */
export function pluralProspects(count: number): string {
    return `${String(count)} ${count === 1 ? 'prospecto' : 'prospectos'}`;
}

/** Cuántos caracteres del final del folio se pintan en la tabla y en el encabezado de la ficha. */
const SHORT_FOLIO_CHARS = 6;

/**
 * El folio recortado por la COLA: es un ULID, y los del mismo día comparten el prefijo, así que el
 * final es la única parte que distingue una fila de otra. El valor completo va en el `title`.
 */
export function shortFolio(folio: string): string {
    if (folio.length <= SHORT_FOLIO_CHARS + 1) return folio;
    return `…${folio.slice(-SHORT_FOLIO_CHARS)}`;
}

export type EventTone = 'info' | 'muted' | 'success' | 'danger';

export interface ProspectEvent {
    text: string;
    /** Instante ISO del evento. */
    at: string;
    tone: EventTone;
}

/** Con lo que el silo firma lo que escribe solo, sin que ninguna persona se lo pida. */
export const SYSTEM_ACTOR = 'system';

/** Quién abrió la ficha. Es `createdBy` o no se sabe: el vendedor no la abre en la mayoría. */
export function createdByEventText(createdBy: string): string {
    return createdBy === SYSTEM_ACTOR
        ? 'La venta se trabó y el sistema abrió la ficha.'
        : 'El vendedor guardó el prospecto desde el wizard.';
}

/** Quién la marcó perdida. El barrido del silo también escribe fichas, y no es «el vendedor». */
export function lostByEventText(updatedBy: string): string {
    return updatedBy === SYSTEM_ACTOR
        ? 'La ficha quedó marcada como perdida.'
        : 'El vendedor la marcó como perdida.';
}

/**
 * La bitácora se DERIVA del propio prospecto: el silo no guarda historial de eventos, solo el
 * estado actual, sus actores y sus marcas de tiempo.
 */
export function prospectTimeline(prospect: Prospect): ProspectEvent[] {
    const created = createdIso(prospect);
    // El desenlace se fecha con la última escritura de la fila, no con el último contacto: el
    // contacto pasa antes de cerrar y fecharía el desenlace en un instante que no es el suyo.
    const settledAt = new Date(prospect.updatedAt).toISOString();
    const events: ProspectEvent[] = [
        { text: createdByEventText(prospect.createdBy), at: created, tone: 'info' },
        {
            text: `Motivo del pending: ${prospectReasonText(prospect)}.`,
            at: created,
            tone: 'muted',
        },
    ];
    if (prospect.lastContactAt) {
        events.push({
            text: 'Se reabrió el wizard y quedó registrado el contacto.',
            at: prospect.lastContactAt,
            tone: 'success',
        });
    }
    if (prospect.status === 'CLOSED' && prospect.saleId) {
        events.push({
            text: `Venta completada: ${prospect.saleId}.`,
            at: settledAt,
            tone: 'success',
        });
    }
    if (prospect.status === 'LOST') {
        events.push({
            text: lostByEventText(prospect.updatedBy),
            at: settledAt,
            tone: 'danger',
        });
    }
    return events;
}
